/**
 * Custom rental flow and bond information for product pages.
 *
 * This script is intentionally split into small components to avoid spaghetti
 * logic and make maintenance easier.
 */

document.addEventListener('DOMContentLoaded', function () {
  const flow = new RentalFlow();
  flow.init();

  initMobileGallerySwipe();
  initLinkedRentalRemoval();
});

class BondManager {
  getBondAmountFromProduct() {
    const fee = window.productBondFee;
    if (fee === undefined || fee === null) {
      return null;
    }

    const normalized = typeof fee === 'number' ? fee : parseFloat(String(fee).replace(/[^0-9.]/g, ''));
    return Number.isFinite(normalized) ? normalized : null;
  }

  determineBondAmount() {
    const configuredFee = this.getBondAmountFromProduct();
    return configuredFee !== null && configuredFee > 0 ? configuredFee : null;
  }

  getBondVariant() {
    const bondAmount = this.determineBondAmount();
    if (bondAmount === null || !Array.isArray(window.rentalBondVariants)) {
      return null;
    }

    const bondAmountInCents = Math.round(bondAmount * 100);
    return window.rentalBondVariants.find((variant) => {
      return variant.available && Number(variant.price) === bondAmountInCents;
    }) || null;
  }
}

class ProductFormService {
  constructor() {
    this.form = document.querySelector('form[data-product-form]');
  }

  get isReady() {
    return !!this.form;
  }

  updateHiddenInputs() {
    const dateInput = document.getElementById('izy_rent_start_date');
    const flatpickrInput = document.querySelector('.izyDate.flatpickr-input');
    if (dateInput && flatpickrInput && flatpickrInput.value.trim()) {
      dateInput.value = flatpickrInput.value;
    }

    const selectedColorInput = document.getElementById('selected-color-input');
    if (selectedColorInput && !selectedColorInput.value.trim()) {
      const selectedColorButton = document.querySelector('.color-swatch-button.selected');
      const colorText = selectedColorButton ? (selectedColorButton.getAttribute('data-color') || selectedColorButton.textContent.trim()) : '';
      if (colorText) {
        selectedColorInput.value = colorText;
      }
    }
  }

  buildPayload() {
    if (!this.form) {
      throw new Error('Product form not found');
    }

    const formData = new FormData(this.form);
    const payload = { properties: {} };

    for (const [name, value] of formData.entries()) {
      if (!value) continue;

      if (name === 'id') {
        payload.id = value;
        continue;
      }

      if (name === 'quantity') {
        payload.quantity = parseInt(value, 10) || 1;
        continue;
      }

      const propertyMatch = name.match(/^properties\[(.*)\]$/);
      if (propertyMatch) {
        payload.properties[propertyMatch[1]] = value;
        continue;
      }

      payload[name] = value;
    }

    if (!payload.id) {
      throw new Error('No product variant selected');
    }

    if (!payload.quantity) {
      payload.quantity = 1;
    }

    return payload;
  }

  async addProductToCart(bondVariant) {
    this.updateHiddenInputs();
    const rentalItem = this.buildPayload();
    const items = [];

    if (bondVariant) {
      items.push({
        id: bondVariant.id,
        quantity: rentalItem.quantity,
        properties: {
          _rental_product_variant: String(rentalItem.id),
        },
      });
    }

    // Shopify returns newly added cart lines in reverse order. Add the bond
    // first so it appears immediately beneath its rental in the cart.
    items.push(rentalItem);

    return this.fetchJson('/cart/add.js', { items });
  }

  async fetchJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw error;
    }

    return response.json();
  }
}

class RentalFlow {
  constructor() {
    this.dom = {
      bookNowBtn: document.getElementById('bookNowBtn'),
      rentalModal: document.getElementById('rentalReminderModal'),
      closeReminderBtn: document.getElementById('closeReminderBtn'),
      rentalConfirmBtn: document.getElementById('rentalConfirmBtn'),
      instagramConfirmModal: document.getElementById('instagramConfirmModal'),
      cancelInstagramModalBtn: document.getElementById('cancelInstagramModalBtn'),
      submitInstagramHandle: document.getElementById('submitInstagramHandle'),
      instagramHandleInput: document.getElementById('instagramHandle'),
      mobileNumberInput: document.getElementById('mobileNumber'),
      emailAddressInput: document.getElementById('emailAddress'),
      rentalFormError: document.getElementById('rentalFormError'),
      rentalFormErrorTitle: document.querySelector('[data-rental-error-title]'),
      rentalFormErrorMessage: document.querySelector('[data-rental-error-message]'),
      bondBreakdownModal: document.getElementById('bondBreakdownModal'),
      closeBondModal: document.getElementById('closeBondModal'),
      closeBondModalBtn: document.getElementById('closeBondModalBtn'),
      bondDetails: document.getElementById('bondDetails'),
    };

    this.productFormService = new ProductFormService();
    this.bondManager = new BondManager();
    this.rentNowClicked = false;
    this.continueToReminderAfterBond = false;
  }

  init() {
    this.initBondBreakdown();
    this.attachModalHandlers();
    this.attachBookNowHandlers();
    this.attachColorSelectionHandler();
  }

  initBondBreakdown() {
    const rawPrice = this.getProductPrice();
    const bondAmount = this.bondManager.determineBondAmount();

    if (bondAmount === null) return;

    if (this.dom.bondDetails) {
      this.dom.bondDetails.innerHTML =
        '<strong>Rental price:</strong> $' + rawPrice.toFixed(0) + '<br>' +
        '<strong>Refundable bond:</strong> $' + bondAmount.toFixed(0);
    }
  }

  attachModalHandlers() {
    this.dom.closeBondModal?.addEventListener('click', () => {
      this.continueToReminderAfterBond = false;
      if (this.dom.bondBreakdownModal) this.dom.bondBreakdownModal.style.display = 'none';
    });

    this.dom.closeBondModalBtn?.addEventListener('click', () => {
      if (this.dom.bondBreakdownModal) this.dom.bondBreakdownModal.style.display = 'none';

      if (this.continueToReminderAfterBond) {
        this.continueToReminderAfterBond = false;
        this.showRentalModal();
      }
    });

    this.dom.rentalConfirmBtn?.addEventListener('click', () => {
      this.rentNowClicked = true;
      this.dom.rentalModal.style.display = 'none';
      this.dom.instagramConfirmModal.style.display = 'block';
    });

    this.dom.closeReminderBtn?.addEventListener('click', () => {
      if (this.dom.rentalModal) this.dom.rentalModal.style.display = 'none';
    });

    this.dom.cancelInstagramModalBtn?.addEventListener('click', () => {
      this.rentNowClicked = false;
      this.clearFormError();
      if (this.dom.instagramConfirmModal) this.dom.instagramConfirmModal.style.display = 'none';
    });

    this.dom.submitInstagramHandle?.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleSubmitInstagram();
    });
  }

  attachBookNowHandlers() {
    if (!this.dom.bookNowBtn) return;

    this.dom.bookNowBtn.addEventListener('click', (event) => {
      event.preventDefault();
      this.openRentalReminder();
    }, true);

    let processing = false;
    this.dom.bookNowBtn.addEventListener('click', (event) => {
      if (processing) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      processing = true;
      this.dom.bookNowBtn.disabled = true;
      this.dom.bookNowBtn.style.opacity = '0.6';
      this.dom.bookNowBtn.style.pointerEvents = 'none';

      setTimeout(() => {
        processing = false;
        this.dom.bookNowBtn.disabled = false;
        this.dom.bookNowBtn.style.opacity = '';
        this.dom.bookNowBtn.style.pointerEvents = '';
      }, 6000);
    }, true);
  }

  attachColorSelectionHandler() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.color-swatch-button');
      if (!button) return;

      document.querySelectorAll('.color-swatch-button.selected').forEach((selected) => {
        selected.classList.remove('selected');
      });

      button.classList.add('selected');
      document.querySelector('#error_color')?.remove();
    });
  }

  async handleSubmitInstagram() {
    this.clearFormError();

    const handle = this.dom.instagramHandleInput?.value?.trim();
    if (!handle) {
      this.showFormError(
        'Please enter your Instagram handle before submitting.',
        'Instagram handle required'
      );
      return;
    }

    const selectedValue = document.querySelector('.izyDate.flatpickr-input')?.value?.trim();
    if (!selectedValue) {
      const startDateInput = document.querySelector('#startDate');
      const errorDate = document.querySelector('#error_date');
      if (errorDate) errorDate.style.display = 'block';
      startDateInput?.classList.add('error');
      return;
    }

    const selectedColor = document.querySelector('.color-swatch-button.selected');
    const colorWrapper = document.querySelector('.color-swatches-wrapper');
    if (document.querySelectorAll('.color-swatch-button').length > 0 && !selectedColor) {
      const colorError = document.createElement('div');
      colorError.id = 'error_color';
      colorError.textContent = 'Please select a color';
      colorError.style.color = 'red';
      colorError.style.fontSize = '13px';
      colorError.style.marginTop = '4px';
      colorWrapper?.appendChild(colorError);
      return;
    }

    if (document.querySelector('#error_date')) {
      document.querySelector('#error_date').style.display = 'none';
    }
    document.querySelector('#error_color')?.remove();

    this.rentNowClicked = false;
    if (this.dom.instagramConfirmModal) this.dom.instagramConfirmModal.style.display = 'none';

    const submitBtn = this.dom.submitInstagramHandle;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding to cart...';
    }

    const loadingDiv = document.createElement('div');
    loadingDiv.id = 'rentalLoadingOverlay';
    loadingDiv.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';
    loadingDiv.innerHTML = '<div style="background:white;padding:30px;border-radius:10px;text-align:center;font-size:16px;">Adding to cart...<br><small style="color:#999;margin-top:8px;display:block;">Please wait</small></div>';
    document.body.appendChild(loadingDiv);

    try {
      if (!this.productFormService.isReady) {
        throw new Error('Product form is not available.');
      }

      const bondVariant = this.bondManager.getBondVariant();
      if (!bondVariant) {
        const bondError = new Error('No available Rental Bond variant matches this product.');
        bondError.code = 'BOND_CONFIGURATION';
        throw bondError;
      }

      await this.productFormService.addProductToCart(bondVariant);
      await this.updateCartNote(handle, this.dom.mobileNumberInput?.value?.trim() || '', this.dom.emailAddressInput?.value?.trim() || '');
      window.location.href = '/cart';
    } catch (error) {
      console.error('Rental add-to-cart error:', error);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit';
      }
      document.getElementById('rentalLoadingOverlay')?.remove();

      const errorContent = this.getAddToCartError(error);
      if (this.dom.instagramConfirmModal) {
        this.dom.instagramConfirmModal.style.display = 'block';
      }
      this.showFormError(errorContent.message, errorContent.title);
    }
  }

  getAddToCartError(error) {
    if (error?.code === 'BOND_CONFIGURATION') {
      return {
        title: 'Bond unavailable',
        message:
          'We couldn’t match the refundable bond for this garment. Please message us on Instagram so we can complete your booking.',
      };
    }

    const errorDescription = [error?.description, error?.message]
      .filter(Boolean)
      .join(' ');
    const isUnavailable =
      error?.status === 422 ||
      /sold out|out of stock|not available|unavailable|inventory/i.test(errorDescription);

    if (isUnavailable) {
      return {
        title: 'Rental unavailable',
        message:
          'This item is sold out or no longer available. Please choose another garment or message us on Instagram for help.',
      };
    }

    return {
      title: 'We couldn’t add this rental',
      message: 'Please check your details and try again. Your information has been kept.',
    };
  }

  showFormError(message, title) {
    if (!this.dom.rentalFormError) return;

    if (this.dom.rentalFormErrorTitle) {
      this.dom.rentalFormErrorTitle.textContent = title;
    }
    if (this.dom.rentalFormErrorMessage) {
      this.dom.rentalFormErrorMessage.textContent = message;
    }

    this.dom.rentalFormError.hidden = false;
    this.dom.rentalFormError.focus();
  }

  clearFormError() {
    if (this.dom.rentalFormError) {
      this.dom.rentalFormError.hidden = true;
    }
  }

  async updateCartNote(handle, mobile, email) {
    const note = ['Instagram: ' + handle, mobile ? 'Mobile: ' + mobile : '', email ? 'Email: ' + email : '']
      .filter(Boolean)
      .join(' | ');

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    }).then((response) => {
      if (!response.ok) {
        return response.json().then((json) => Promise.reject(json));
      }
      return response.json();
    });
  }

  openRentalReminder() {
    const bondAmount = this.bondManager.determineBondAmount();

    if (bondAmount !== null && this.dom.bondBreakdownModal) {
      this.continueToReminderAfterBond = true;
      this.dom.bondBreakdownModal.style.display = 'block';
      return;
    }

    this.showRentalModal();
  }

  showRentalModal() {
    if (this.dom.rentalModal) {
      this.dom.rentalModal.style.display = 'block';
    }
  }

  getProductPrice() {
    const priceEl = document.querySelector('.price-item--regular')
      || document.querySelector('[data-product-price]')
      || document.querySelector('.product__price');

    const priceText = priceEl ? priceEl.innerText.replace(/[^0-9.]/g, '') : '';
    return parseFloat(priceText) || 0;
  }
}

function initMobileGallerySwipe() {
  if (window.innerWidth > 749) return;

  const mobileGallery = document.querySelector('.product__media-container.below-mobile:not(.carousel)');
  if (!mobileGallery) return;

  const galleryStyle = mobileGallery.getAttribute('data-gallery-style');
  if (galleryStyle === 'carousel') return;

  const mediaItems = mobileGallery.querySelectorAll('.product__media-item');
  if (mediaItems.length <= 1) return;

  let currentIndex = 0;
  let startX = 0;
  let startY = 0;
  let isSwiping = false;
  let isHorizontalSwipe = false;

  function updateCurrentIndex() {
    currentIndex = Array.from(mediaItems).findIndex((item) =>
      !item.classList.contains('hidden') && !item.classList.contains('product__media-item--hide-on-load')
    );
    if (currentIndex === -1) currentIndex = 0;
  }

  updateCurrentIndex();

  mobileGallery.addEventListener('touchstart', (event) => {
    if (event.touches.length > 1) return;
    startX = event.touches[0].clientX;
    startY = event.touches[0].clientY;
    isSwiping = true;
    isHorizontalSwipe = false;
  });

  mobileGallery.addEventListener('touchmove', (event) => {
    if (!isSwiping) return;
    const diffX = startX - event.touches[0].clientX;
    const diffY = startY - event.touches[0].clientY;
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      event.preventDefault();
      isHorizontalSwipe = true;
      mobileGallery.style.cursor = 'grab';
    }
  });

  mobileGallery.addEventListener('touchend', (event) => {
    if (!isSwiping) return;
    isSwiping = false;
    mobileGallery.style.cursor = 'auto';
    if (!isHorizontalSwipe) return;

    const diff = startX - event.changedTouches[0].clientX;
    if (Math.abs(diff) < 30) return;

    if (diff > 0 && currentIndex < mediaItems.length - 1) currentIndex++;
    else if (diff < 0 && currentIndex > 0) currentIndex--;

    mediaItems.forEach((item, index) => {
      if (index === currentIndex) {
        item.classList.remove('hidden', 'product__media-item--hide-on-load');
      } else {
        item.classList.add('hidden');
      }
    });
    updateCurrentIndex();
  });
}

function initLinkedRentalRemoval() {
  document.addEventListener('click', async (event) => {
    const removeButton = event.target.closest('[data-remove-item][data-also-remove-key]');
    if (!removeButton || removeButton.dataset.removingLinkedItem === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const cartItem = removeButton.closest('[data-input-item]');
    const bondKey = removeButton.dataset.alsoRemoveKey;
    if (!cartItem || !bondKey) return;

    removeButton.dataset.removingLinkedItem = 'true';
    removeButton.disabled = true;
    cartItem.classList.add('has-quantity-update');

    const configuredRoute = window.theme?.routes?.cart?.change || '/cart/change';
    const cartChangeRoute = configuredRoute.endsWith('.js')
      ? configuredRoute
      : configuredRoute + '.js';

    try {
      const response = await fetch(cartChangeRoute, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: bondKey,
          quantity: 0,
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to remove the linked rental bond.');
      }

      removeButton.removeAttribute('data-also-remove-key');
      delete removeButton.dataset.removingLinkedItem;
      removeButton.disabled = false;
      removeButton.click();
    } catch (error) {
      console.error('Linked rental removal error:', error);
      removeButton.disabled = false;
      delete removeButton.dataset.removingLinkedItem;
      cartItem.classList.remove('has-quantity-update');

      const errorMessage = cartItem.querySelector(
        '.cart__form-item-error, .quick-cart__item-error'
      );
      if (errorMessage) {
        errorMessage.textContent =
          'We couldn’t remove this rental and its bond. Please try again.';
        errorMessage.classList.remove('hidden');
      }
    }
  }, true);
}

function scrollToTop() {
  const modal = document.getElementById('instagramModalContent');
  if (modal) modal.scrollTo({ top: 0, behavior: 'smooth' });
}

function scrollToBottom() {
  const modal = document.getElementById('instagramModalContent');
  if (modal) modal.scrollTo({ top: modal.scrollHeight, behavior: 'smooth' });
}
